<!-- src/views/pages/administration/reports/task-report.vue -->
<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";

import { ref, computed, onMounted } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { useTaskStore } from "@/stores/taskStore";
import { useProjectStore } from "@/stores/projectStore";
import TaskReportTable from "./task-report-table.vue";

const title = ref("Task Report");
const text = ref("HR");
const text1 = ref("Task Report");

const dateRangeInput = ref(null);

const taskStore = useTaskStore();
const projectStore = useProjectStore();

// FILTER STATE (labels for dropdown buttons)
const priorityLabel = ref("Select Priority");
const statusLabel = ref("Select Status");
const sortLabel = ref("Last 7 Days");
const selectedProjectLabel = ref("All Projects");

// --------- HELPERS ----------
function percent(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// --------- LOAD DATA ----------
async function reloadTasks(extra = {}) {
  await taskStore.list({
    ...extra,
    paginate: false, // for reports we want full dataset under current filters
    page: 1,
    per_page: 5000,
  });
}

async function initData() {
  await Promise.all([
    projectStore.list({ paginate: false, per_page: 5000 }),
    reloadTasks(),
  ]);
}

// --------- PROJECT OPTIONS ----------
const projectOptions = computed(() =>
  projectStore.items.map((p) => ({
    id: p.id,
    label: p.name || p.project_code || `Project #${p.id}`,
  }))
);

function chooseProject(project) {
  if (!project) {
    taskStore.filters.project_id = "";
    selectedProjectLabel.value = "All Projects";
  } else {
    taskStore.filters.project_id = project.id;
    selectedProjectLabel.value = project.label;
  }
  reloadTasks();
}

// --------- FILTER DROPDOWNS ----------
function setPriority(value) {
  taskStore.filters.priority = value || "";
  priorityLabel.value = value ? value : "Select Priority";
  reloadTasks();
}

function setStatus(value) {
  taskStore.filters.status = value || "";
  statusLabel.value = value ? value : "Select Status";
  reloadTasks();
}

function applySortPreset(preset) {
  const now = moment();
  // reset date filters by default
  taskStore.filters.due_from = "";
  taskStore.filters.due_to = "";

  if (preset === "recent") {
    sortLabel.value = "Recently Added";
    taskStore.filters.sort = "created";
  } else if (preset === "last_month") {
    const start = moment(now).subtract(1, "month").startOf("month");
    const end = moment(now).subtract(1, "month").endOf("month");
    taskStore.filters.due_from = start.format("YYYY-MM-DD");
    taskStore.filters.due_to = end.format("YYYY-MM-DD");
    sortLabel.value = "Last Month";
    taskStore.filters.sort = "due";
  } else if (preset === "last_7_days") {
    const start = moment(now).subtract(6, "days");
    const end = moment(now);
    taskStore.filters.due_from = start.format("YYYY-MM-DD");
    taskStore.filters.due_to = end.format("YYYY-MM-DD");
    sortLabel.value = "Last 7 Days";
    taskStore.filters.sort = "due";
  } else if (preset === "priority") {
    sortLabel.value = "Priority";
    taskStore.filters.sort = "priority";
  }

  reloadTasks();
}

// --------- SUMMARY STATS ----------
const totalTasks = computed(
  () => taskStore.meta.total || taskStore.items.length || 0
);

const statusCounts = computed(() => {
  const base = { Completed: 0, Pending: 0, Inprogress: 0, Onhold: 0 };
  for (const t of taskStore.items) {
    if (base.hasOwnProperty(t.status)) base[t.status]++;
  }
  return base;
});

const priorityCounts = computed(() => {
  const base = { High: 0, Medium: 0, Low: 0 };
  for (const t of taskStore.items) {
    if (base.hasOwnProperty(t.priority)) base[t.priority]++;
  }
  return base;
});

const visibilityCounts = computed(() => {
  const base = { Public: 0, Private: 0, Admin: 0 };
  for (const t of taskStore.items) {
    if (base.hasOwnProperty(t.visibility)) base[t.visibility]++;
  }
  return base;
});

const overdueCounts = computed(() => {
  const now = moment().startOf("day");
  let overdue = 0;
  let ontime = 0;
  for (const t of taskStore.items) {
    if (!t.due_date) continue;
    const due = moment(t.due_date);
    if (due.isBefore(now) && t.status !== "Completed") overdue++;
    else ontime++;
  }
  return { overdue, ontime };
});

const statusPercents = computed(() => {
  const total = totalTasks.value;
  const c = statusCounts.value;
  return {
    completed: percent(c.Completed, total),
    pending: percent(c.Pending, total),
    inprogress: percent(c.Inprogress, total),
    onhold: percent(c.Onhold, total),
  };
});

// --------- APEX DONUT CONFIGS ----------

// 1) By Priority
const donutPrioritySeries = computed(() => {
  const c = priorityCounts.value;
  return [c.High, c.Medium, c.Low];
});
const donutPriorityOptions = computed(() => ({
  labels: ["High", "Medium", "Low"],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
}));

// 2) By Status
const donutStatusSeries = computed(() => {
  const c = statusCounts.value;
  return [c.Completed, c.Pending, c.Inprogress, c.Onhold];
});
const donutStatusOptions = computed(() => ({
  labels: ["Completed", "Pending", "Inprogress", "Onhold"],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
}));

// 3) By Visibility
const donutVisibilitySeries = computed(() => {
  const c = visibilityCounts.value;
  return [c.Public, c.Private, c.Admin];
});
const donutVisibilityOptions = computed(() => ({
  labels: ["Public", "Private", "Admin"],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
}));

// 4) Overdue vs On-time
const donutOverdueSeries = computed(() => {
  const c = overdueCounts.value;
  return [c.overdue, c.ontime];
});
const donutOverdueOptions = computed(() => ({
  labels: ["Overdue", "On Time"],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
}));

// Main "Tasks" donut (status breakdown)
const tasksDonutSeries = donutStatusSeries;
const tasksDonutOptions = donutStatusOptions;

// --------- DATE RANGE PICKER ----------
onMounted(async () => {
  await initData();

  if (dateRangeInput.value) {
    const start = moment().subtract(6, "days");
    const end = moment();

    // initialize filters to last 7 days
    taskStore.filters.due_from = start.format("YYYY-MM-DD");
    taskStore.filters.due_to = end.format("YYYY-MM-DD");
    sortLabel.value = "Last 7 Days";
    await reloadTasks();

    // init DateRangePicker
    // callback fires whenever range changes
    // eslint-disable-next-line no-new
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      function (start, end) {
        taskStore.filters.due_from = start.format("YYYY-MM-DD");
        taskStore.filters.due_to = end.format("YYYY-MM-DD");
        sortLabel.value = `${start.format("DD MMM YYYY")} - ${end.format(
          "DD MMM YYYY"
        )}`;
        reloadTasks();
      }
    );
  }
});

function exportAs(format) {
  taskStore.downloadReport(format);
}


// toggle header (unchanged)
function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('xlsx')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              class=""
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid">
        <!-- SUMMARY CARDS + SMALL DONUTS -->
        <div class="col-lg-6 col-md-6 d-flex">
          <div class="row flex-fill">
            <!-- By Priority -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-8">
                      <span class="fs-14 fw-normal text-truncate mb-1">
                        Tasks by Priority
                      </span>
                      <h5>{{ totalTasks }}</h5>
                    </div>
                    <div class="col-4">
                      <apexchart
                        type="donut"
                        height="200"
                        :options="donutPriorityOptions"
                        :series="donutPrioritySeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- By Status -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-8">
                      <span class="fs-14 fw-normal text-truncate mb-1">
                        Tasks by Status
                      </span>
                      <h5>{{ totalTasks }}</h5>
                    </div>
                    <div class="col-4">
                      <apexchart
                        type="donut"
                        height="200"
                        :options="donutStatusOptions"
                        :series="donutStatusSeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- By Visibility -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-8">
                      <span class="fs-14 fw-normal text-truncate mb-1">
                        Tasks by Visibility
                      </span>
                      <h5>{{ totalTasks }}</h5>
                    </div>
                    <div class="col-4">
                      <apexchart
                        type="donut"
                        height="200"
                        :options="donutVisibilityOptions"
                        :series="donutVisibilitySeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Overdue vs On Time -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-8">
                      <span class="fs-14 fw-normal text-truncate mb-1">
                        Overdue vs On Time
                      </span>
                      <h5>{{ totalTasks }}</h5>
                    </div>
                    <div class="col-4">
                      <apexchart
                        type="donut"
                        height="200"
                        :options="donutOverdueOptions"
                        :series="donutOverdueSeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MAIN TASKS DONUT -->
        <div class="col-lg-6 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center row-gap-3"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2">
                    <i class="ti ti-chart-pie text-danger"></i>
                  </span>
                  <h5>Tasks</h5>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedProjectLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="chooseProject(null)"
                      >
                        All Projects
                      </a>
                    </li>
                    <li v-for="p in projectOptions" :key="p.id">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="chooseProject(p)"
                      >
                        {{ p.label }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="card-body pt-0">
              <div class="row align-items-center">
                <div
                  class="col-md-6 d-flex align-items-center justify-content-center"
                >
                  <div class="position-relative payment-total">
                    <div id="task-reports">
                      <apexchart
                        type="donut"
                        height="180"
                        :options="tasksDonutOptions"
                        :series="tasksDonutSeries"
                      ></apexchart>
                    </div>
                    <div class="task-total-content">
                      <p class="fs-16 fw-normal mb-0">Pending</p>
                      <span class="display-3 fs-24 fw-bold text-skyblue">
                        {{ statusPercents.pending }}%
                      </span>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="row gy-4">
                    <div class="col-md-6">
                      <div class="d-flex task-report-icons">
                        <span class="me-2">
                          <i
                            class="ti ti-arrow-badge-right-filled text-success"
                          ></i>
                        </span>
                        <h6 class="fs-16">
                          Completed
                          <span class="fs-14 fw-normal">
                            {{ statusPercents.completed }}%
                          </span>
                        </h6>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex task-report-icons">
                        <span class="me-2">
                          <i
                            class="ti ti-arrow-badge-right-filled text-skyblue"
                          ></i>
                        </span>
                        <h6 class="fs-16">
                          Pending
                          <span class="fs-14 fw-normal">
                            {{ statusPercents.pending }}%
                          </span>
                        </h6>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex task-report-icons">
                        <span class="me-2">
                          <i
                            class="ti ti-arrow-badge-right-filled text-warning"
                          ></i>
                        </span>
                        <h6 class="fs-16">
                          Inprogress
                          <span class="fs-14 fw-normal">
                            {{ statusPercents.inprogress }}%
                          </span>
                        </h6>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex task-report-icons">
                        <span class="me-2">
                          <i
                            class="ti ti-arrow-badge-right-filled text-purple"
                          ></i>
                        </span>
                        <h6 class="fs-16">
                          On Hold
                          <span class="fs-14 fw-normal">
                            {{ statusPercents.onhold }}%
                          </span>
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /row -->
            </div>
          </div>
        </div>
      </div>

      <!-- TASKS LIST + FILTER BAR -->
      <div class="card mt-3">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Tasks List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <!-- Date Range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Priority -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ priorityLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('')"
                  >
                    All
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('Low')"
                  >
                    Low
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('Medium')"
                  >
                    Medium
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPriority('High')"
                  >
                    High
                  </a>
                </li>
              </ul>
            </div>

            <!-- Status -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ statusLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('')"
                  >
                    All
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Pending')"
                  >
                    Pending
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Inprogress')"
                  >
                    Inprogress
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Completed')"
                  >
                    Completed
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Onhold')"
                  >
                    On Hold
                  </a>
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="applySortPreset('recent')"
                  >
                    Recently Added
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="applySortPreset('priority')"
                  >
                    Priority
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="applySortPreset('last_month')"
                  >
                    Last Month
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="applySortPreset('last_7_days')"
                  >
                    Last 7 Days
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <TaskReportTable />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .task-total-content {
    position: static !important;
    text-align: center;
    margin-top: 0.5rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


